import { useState, useRef, useCallback, useEffect } from "react";
import { useAuth } from "@/auth/AuthContext";

const API = import.meta.env.VITE_API_URL || "http://localhost:5001/api";

// ─── LOGO URLS ────────────────────────────────────────────────────────────────
const nishSrc  = new URL('../../assets/images/NISHlogo.png',  import.meta.url).href;
const ncahtSrc = new URL('../../assets/images/NCAHTlogo.png', import.meta.url).href;
const icmrSrc  = new URL('../../assets/images/ICMRlogo.png',  import.meta.url).href;

async function imgToBase64(src) {
  try {
    const res  = await fetch(src);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const LANGUAGES = [
  { code: "en", name: "English"   },
  { code: "ml", name: "മലയാളം"  },
  { code: "hi", name: "हिंदी"    },
  { code: "ta", name: "தமிழ்"   },
  { code: "te", name: "తెలుగు"  },
];

const BORDER_STYLES = [
  { id: "solid",   label: "Solid"   },
  { id: "double",  label: "Double"  },
  { id: "dashed",  label: "Dashed"  },
  { id: "rounded", label: "Rounded" },
  { id: "none",    label: "None"    },
];

const LABEL_POSITIONS = [
  { id: "bottom", label: "Below", icon: "⬇️" },
  { id: "top",    label: "Above", icon: "⬆️" },
  { id: "none",   label: "None",  icon: "🚫" },
];

const THEMES = [
  { id: "white",    label: "Clean White",  bg: "#FFFFFF", accent: "#003366", text: "#1a1a2e" },
  { id: "cream",    label: "Warm Cream",   bg: "#FFFDF5", accent: "#8B4513", text: "#3a2010" },
  { id: "sky",      label: "Sky Blue",     bg: "#F0F8FF", accent: "#1565C0", text: "#0d2a4a" },
  { id: "mint",     label: "Soft Mint",    bg: "#F0FFF8", accent: "#1B6B45", text: "#0d3320" },
  { id: "lavender", label: "Lavender",     bg: "#F8F0FF", accent: "#6A1B9A", text: "#2d0f40" },
  { id: "peach",    label: "Peach",        bg: "#FFF5F0", accent: "#BF360C", text: "#4a1205" },
];

const FONT_SIZES = [
  { id: "sm", label: "Small",   size: 28 },
  { id: "md", label: "Medium",  size: 38 },
  { id: "lg", label: "Large",   size: 52 },
  { id: "xl", label: "X-Large", size: 68 },
];

function resolveUrl(card) {
  if (card.uploadedUrl) return card.uploadedUrl;
  if (card.imageUrl?.startsWith("/uploads")) return `${API.replace("/api","")}${card.imageUrl}`;
  if (card.imageUrl?.startsWith("http") || card.imageUrl?.startsWith("data:")) return card.imageUrl;
  return "";
}

// ─── PDF GENERATOR ────────────────────────────────────────────────────────────
async function generatePDF(cards, settings) {
  const { language, theme, borderStyle, labelPosition, fontSize, showPageNumber, customSubtitle } = settings;
  const themeObj = THEMES.find(t => t.id === theme) || THEMES[0];
  const fontPx   = FONT_SIZES.find(f => f.id === fontSize)?.size || 52;

  // Fetch all three logos as base64
  const [nishB64, ncahtB64, icmrB64] = await Promise.all([
    imgToBase64(nishSrc),
    imgToBase64(ncahtSrc),
    imgToBase64(icmrSrc),
  ]);

  const logoImg = (src, h, alt) => src
    ? `<img src="${src}" style="height:${h}px;width:auto;object-fit:contain;display:block;" alt="${alt}"/>`
    : `<span style="font-size:7px;color:${themeObj.accent};font-weight:700;">${alt}</span>`;

  const PAGE_H   = 842;
  const PAGE_W   = 595;
  const PAD_V    = 28;
  const PAD_H    = 32;
  const HEADER_H = 46;
  const FOOTER_H = 50;
  const LABEL_H  = labelPosition !== "none" ? fontPx * 1.4 + 20 : 0;
  const IMG_H    = PAGE_H - PAD_V*2 - HEADER_H - FOOTER_H - LABEL_H - 8;
  const IMG_W    = PAGE_W - PAD_H*2;

  const getBorder = () => ({
    solid:   `4px solid ${themeObj.accent}`,
    double:  `6px double ${themeObj.accent}`,
    dashed:  `4px dashed ${themeObj.accent}`,
    rounded: `4px solid ${themeObj.accent}`,
    none:    "none",
  }[borderStyle] || `4px solid ${themeObj.accent}`);

  const radius = borderStyle === "rounded" ? "24px" : "8px";

  const pages = cards.map((card, idx) => {
    const label  = card.labels?.[language] || card.labels?.en || card.name || "";
    const imgSrc = resolveUrl(card);

    const labelHtml = labelPosition !== "none" ? `
      <div style="font-family:'Segoe UI',Arial,sans-serif;font-size:${fontPx}px;font-weight:900;
                  color:${themeObj.text};text-align:center;letter-spacing:2px;
                  padding:${labelPosition==="top"?"0 0 16px":"16px 0 0"};
                  text-transform:uppercase;word-break:break-word;line-height:1.2;
                  flex-shrink:0;">${label}</div>` : "";

    return `
    <div style="width:${PAGE_W}px;height:${PAGE_H}px;background:${themeObj.bg};
                display:flex;flex-direction:column;align-items:center;
                padding:${PAD_V}px ${PAD_H}px;
                page-break-after:always;box-sizing:border-box;overflow:hidden;">

      <!-- HEADER -->
      <div style="width:100%;display:flex;align-items:center;justify-content:space-between;
                  margin-bottom:12px;flex-shrink:0;height:34px;">
        <div style="display:flex;align-items:center;gap:8px;">
          ${logoImg(nishB64, 32, "NISH")}
          <div style="font-family:Arial;font-size:7px;color:${themeObj.accent};line-height:1.4;">
            <div style="font-weight:700;">National Institute of Speech &amp; Hearing</div>
            <div style="opacity:.7;">Thiruvananthapuram, Kerala · FSLP</div>
          </div>
        </div>
        ${customSubtitle ? `<div style="font-family:Arial;font-size:9px;font-weight:700;color:${themeObj.accent};">${customSubtitle}</div>` : ""}
        <div style="display:flex;align-items:center;gap:6px;">
          ${logoImg(ncahtB64, 26, "NCAHT")}
          ${logoImg(icmrB64,  26, "ICMR")}
        </div>
      </div>

      ${labelPosition === "top" ? labelHtml : ""}

      <!-- IMAGE BOX -->
      <div style="width:${IMG_W}px;height:${IMG_H}px;flex-shrink:0;
                  border:${getBorder()};border-radius:${radius};overflow:hidden;
                  background:white;display:flex;align-items:center;justify-content:center;">
        <img src="${imgSrc}"
             style="width:100%;height:100%;object-fit:contain;display:block;"
             crossorigin="anonymous"/>
      </div>

      ${labelPosition === "bottom" ? labelHtml : ""}

      <!-- FOOTER -->
      <div style="width:100%;border-top:1.5px solid ${themeObj.accent}40;padding-top:8px;
                  display:flex;justify-content:space-between;align-items:center;
                  margin-top:auto;flex-shrink:0;height:42px;">
        <div style="display:flex;align-items:center;gap:8px;">
          ${logoImg(nishB64,  24, "NISH")}
          ${logoImg(ncahtB64, 20, "NCAHT")}
          ${logoImg(icmrB64,  20, "ICMR")}
        </div>
        ${showPageNumber ? `<span style="font-family:Arial;font-size:7px;color:${themeObj.accent};opacity:.7;">${idx+1} / ${cards.length}</span>` : ""}
      </div>
    </div>`;
  });

  const win = window.open("","_blank");
  if (!win) return;
  win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"/><style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{background:#888;}
    @media print{body{background:white;}@page{size:A4 portrait;margin:0;}div{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
  </style></head><body>${pages.join("")}</body></html>`);
  win.document.close();
  setTimeout(() => win.print(), 700);
}

// ─── IndicatorTagPicker ───────────────────────────────────────────────────────
// Shared chip-picker for the 40 NCF indicators, grouped by domain. Self-contained
// copy of the same component used in CollectionManager / FlashcardGenerator /
// WorksheetCreator — kept local here so this file stays drop-in deployable.
// (Candidate for extraction to a shared component file later.)
function IndicatorTagPicker({ taxonomy, selected, onToggle, compact }) {
  const [openDomain, setOpenDomain] = useState(null);
  if (!taxonomy) return (
    <div style={{ fontSize:11, color:"#aaa" }}>Loading indicator list…</div>
  );
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {taxonomy.domains.map(dom => {
        const inds = taxonomy.indicators.filter(i => i.domainId === dom.id);
        const selCount = inds.filter(i => selected.includes(i.id)).length;
        const open = openDomain === dom.id;
        return (
          <div key={dom.id} style={{ border:"1.5px solid #e0e7f0", borderRadius:9, overflow:"hidden" }}>
            <button onClick={() => setOpenDomain(open ? null : dom.id)}
              style={{ width:"100%", display:"flex", alignItems:"center", gap:8, padding: compact ? "6px 10px" : "8px 12px",
                       background: selCount > 0 ? "#EBF4FB" : "white", border:"none", cursor:"pointer", textAlign:"left" }}>
              <span style={{ fontSize: compact ? 13 : 15 }}>{dom.icon}</span>
              <span style={{ flex:1, fontSize: compact ? 11 : 12, fontWeight:700, color:"#003366" }}>{dom.label}</span>
              {selCount > 0 && (
                <span style={{ fontSize:10, fontWeight:800, color:"#003366", background:"white",
                               border:"1px solid #BDD7EE", borderRadius:10, padding:"1px 7px" }}>{selCount}</span>
              )}
              <span style={{ fontSize:11, color:"#888" }}>{open ? "▲" : "▼"}</span>
            </button>
            {open && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:5, padding:"10px 12px", background:"#FAFCFF" }}>
                {inds.map(ind => {
                  const on = selected.includes(ind.id);
                  return (
                    <button key={ind.id} onClick={() => onToggle(ind.id)}
                      title={ind.label}
                      style={{ padding:"4px 10px", borderRadius:8, fontSize:11, fontWeight:700,
                               border:`1.5px solid ${on ? "#003366" : "#e0e7f0"}`,
                               background: on ? "#003366" : "white",
                               color: on ? "white" : "#555", cursor:"pointer" }}>
                      {ind.id} {on ? "✓" : ""}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── COLLECTION PICKER MODAL ──────────────────────────────────────────────────
function CollectionPickerModal({ getAuthHeader, onPick, onClose }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [picked,  setPicked]  = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const base = API.replace("/api","");
        const res  = await fetch(`${API}/collection?limit=200`, { headers: getAuthHeader() });
        const data = await res.json();
        if (data.success) setItems(data.images.map(doc => ({
          id:       doc._id,
          name:     doc.title || "Image",
          imageUrl: `${base}${doc.url}`,
          labels:   doc.labels || { en: doc.title || "" },
          color:    "#E8F0FE",
          emoji:    "",
          category: doc.category,
          indicators: doc.indicators || [],
        })));
      } catch {}
      setLoading(false);
    })();
  }, []);

  const filtered = items.filter(it =>
    !search || it.name.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = id => setPicked(p => { const s=new Set(p); s.has(id)?s.delete(id):s.add(id); return s; });

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,padding:16}} onClick={onClose}>
      <div style={{background:"#f5f7fa",borderRadius:18,width:800,maxWidth:"96vw",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 60px rgba(0,0,0,.3)"}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:"16px 24px 12px",borderBottom:"1px solid #e0e7f0",display:"flex",alignItems:"center",gap:12,background:"white",borderRadius:"18px 18px 0 0"}}>
          <div style={{fontWeight:800,fontSize:15,color:"#003366",flex:1}}>🗂️ Browse Collection</div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…"
            style={{border:"1.5px solid #dce3f5",borderRadius:8,padding:"6px 12px",fontSize:12,outline:"none",width:200}}/>
          <span style={{fontSize:12,color:"#888"}}>{picked.size} selected</span>
          <button onClick={onClose} style={{background:"#f0f4ff",border:"none",borderRadius:8,padding:"6px 12px",cursor:"pointer",color:"#666"}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:16}}>
          {loading ? <div style={{textAlign:"center",padding:60,fontSize:32,color:"#aaa"}}>⏳</div>
          : filtered.length===0 ? <div style={{textAlign:"center",padding:60,color:"#aaa"}}>No images found</div>
          : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:10}}>
              {filtered.map(item=>{
                const on=picked.has(item.id);
                return (
                  <div key={item.id} onClick={()=>toggle(item.id)}
                    style={{border:`2px solid ${on?"#003366":"#e0e7f0"}`,borderRadius:10,overflow:"hidden",cursor:"pointer",background:"white",position:"relative"}}>
                    {on && <div style={{position:"absolute",top:4,right:4,background:"#003366",color:"white",borderRadius:"50%",width:18,height:18,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:900,zIndex:2}}>✓</div>}
                    {item.indicators?.length > 0 && (
                      <div style={{position:"absolute",top:4,left:4,background:"rgba(0,51,102,.85)",color:"white",fontSize:8.5,fontWeight:800,padding:"1px 5px",borderRadius:5,zIndex:2}}
                        title={item.indicators.join(", ")}>
                        🎯 {item.indicators.length}
                      </div>
                    )}
                    <div style={{height:80,display:"flex",alignItems:"center",justifyContent:"center",background:"#f8f9fc"}}>
                      <img src={item.imageUrl} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}} alt=""/>
                    </div>
                    <div style={{padding:"4px 6px",fontSize:9.5,fontWeight:700,color:"#1a1a2e",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</div>
                  </div>
                );
              })}
            </div>}
        </div>
        <div style={{padding:"12px 24px",borderTop:"1px solid #e0e7f0",background:"white",borderRadius:"0 0 18px 18px",display:"flex",justifyContent:"flex-end",gap:10}}>
          <button onClick={onClose} style={{padding:"8px 18px",background:"white",color:"#666",border:"1px solid #dce3f5",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={()=>onPick(items.filter(i=>picked.has(i.id)))} disabled={picked.size===0}
            style={{padding:"8px 20px",background:picked.size>0?"#003366":"#ccc",color:"white",border:"none",borderRadius:8,fontWeight:700,fontSize:13,cursor:picked.size>0?"pointer":"default"}}>
            Add {picked.size>0?`${picked.size} Image${picked.size>1?"s":""}`:"Images"} →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SAVED SETS PANEL ─────────────────────────────────────────────────────────
function SavedSetsPanel({ getAuthHeader, onLoad, onClose }) {
  const [sets,     setSets]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const s    = search ? `&search=${encodeURIComponent(search)}` : "";
      const res  = await fetch(`${API}/activitycard?limit=50${s}`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success) setSets(data.activityCards);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [search]);

  const del = async id => {
    setDeleting(id);
    try {
      await fetch(`${API}/activitycard/${id}`, { method:"DELETE", headers:getAuthHeader() });
      setSets(p => p.filter(s => s._id !== id));
    } catch {}
    setDeleting(null);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,padding:16}} onClick={onClose}>
      <div style={{background:"#f5f7fa",borderRadius:18,width:840,maxWidth:"96vw",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 60px rgba(0,0,0,.3)"}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:"16px 24px 12px",borderBottom:"1px solid #e0e7f0",display:"flex",alignItems:"center",gap:12,background:"white",borderRadius:"18px 18px 0 0"}}>
          <div style={{fontWeight:800,fontSize:15,color:"#003366",flex:1}}>📂 Saved Card Sets</div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…"
            style={{border:"1.5px solid #dce3f5",borderRadius:8,padding:"6px 12px",fontSize:12,outline:"none",width:200}}/>
          <button onClick={onClose} style={{background:"#f0f4ff",border:"none",borderRadius:8,padding:"6px 12px",cursor:"pointer",color:"#666"}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:16}}>
          {loading ? <div style={{textAlign:"center",padding:60,fontSize:32,color:"#aaa"}}>⏳</div>
          : sets.length===0 ? <div style={{textAlign:"center",padding:60,color:"#aaa"}}>No saved card sets yet</div>
          : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:12}}>
              {sets.map(s=>(
                <div key={s._id} style={{background:"white",borderRadius:12,padding:16,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:6}}>
                    <div style={{fontWeight:700,fontSize:13,color:"#1a1a2e",marginBottom:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{s.title}</div>
                    {s.indicators?.length > 0 && (
                      <span style={{fontSize:9,fontWeight:700,color:"#003366",background:"#EBF4FB",padding:"1px 6px",borderRadius:6,flexShrink:0}}>
                        🎯 {s.indicators.length}
                      </span>
                    )}
                  </div>
                  <div style={{fontSize:10,color:"#888",marginBottom:4}}>{s.subtitle}</div>
                  <div style={{fontSize:10,color:"#aaa",marginBottom:12}}>
                    Used {s.usedCount||0}× · {new Date(s.createdAt).toLocaleDateString()}
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>onLoad(s)} style={{flex:1,padding:"7px 0",background:"#003366",color:"white",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>Load & Edit</button>
                    <button onClick={()=>del(s._id)} style={{padding:"7px 12px",background:"#fff0f0",color:"#c0392b",border:"1px solid #f5c6c6",borderRadius:7,fontSize:12,cursor:"pointer"}}>
                      {deleting===s._id?"…":"🗑"}
                    </button>
                  </div>
                </div>
              ))}
            </div>}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function ActivityCards() {
  const { getAuthHeader } = useAuth();

  const [selectedCards,    setSelectedCards]    = useState([]);
  const [uploadedCards,    setUploadedCards]    = useState([]);
  const [language,         setLanguage]         = useState("en");
  const [theme,            setTheme]            = useState("white");
  const [borderStyle,      setBorderStyle]      = useState("solid");
  const [labelPosition,    setLabelPosition]    = useState("bottom");
  const [fontSize,         setFontSize]         = useState("lg");
  const [showPageNumber,   setShowPageNumber]   = useState(true);
  const [customSubtitle,   setCustomSubtitle]   = useState("Activity Cards");
  const [searchQuery,      setSearchQuery]      = useState("");
  const [activeTab,        setActiveTab]        = useState("select");
  const [showPicker,       setShowPicker]       = useState(false);
  const [showSaved,        setShowSaved]        = useState(false);
  const [saving,           setSaving]           = useState(false);
  const [saveMsg,          setSaveMsg]          = useState("");
  const [savedId,          setSavedId]          = useState(null);
  const [setTitle,         setSetTitle]         = useState("My Activity Cards");
  const fileInputRef = useRef();

  // Shared NCF development taxonomy (40 indicators / 5 domains) — same source
  // as CollectionManager / FlashcardGenerator / WorksheetCreator. Used to tag
  // this card set (and its cards) so SM-2/BKT engagement can later roll up
  // to NCF indicator progress.
  const [taxonomy, setTaxonomy] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res  = await fetch(`${API}/collection/taxonomy`, { headers: getAuthHeader() });
        const data = await res.json();
        if (data.success) setTaxonomy(data.taxonomy);
      } catch {}
    })();
  }, []);

  // Set-level NCF indicator tags — the primary tag for this card set as a
  // whole. Individual cards can also carry their own tags (inherited from
  // Collection when picked).
  const [setIndicators, setSetIndicators] = useState([]);
  const toggleSetIndicator = (id) => {
    setSetIndicators(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  };

  const themeObj = THEMES.find(t => t.id === theme) || THEMES[0];
  const fontPx   = FONT_SIZES.find(f => f.id === fontSize)?.size || 52;

  const filteredCards = searchQuery
    ? uploadedCards.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        Object.values(c.labels||{}).some(v => v.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : uploadedCards;

  const toggleCard = card => {
    setSelectedCards(prev =>
      prev.find(c => c.id === card.id) ? prev.filter(c => c.id !== card.id) : [...prev, card]
    );
  };
  const isSelected = card => selectedCards.some(c => c.id === card.id);

  // Domains touched by this card set right now (set tags ∪ every selected
  // card's tags) — purely a live preview; the server derives & stores the
  // authoritative `domains` field on save.
  const touchedDomains = (() => {
    if (!taxonomy) return [];
    const all = new Set(setIndicators);
    selectedCards.forEach(c => (c.indicators || []).forEach(id => all.add(id)));
    const domainIds = new Set();
    taxonomy.indicators.forEach(ind => { if (all.has(ind.id)) domainIds.add(ind.domainId); });
    return taxonomy.domains.filter(d => domainIds.has(d.id));
  })();

  const handleFileUpload = useCallback((files) => {
    Array.from(files).forEach(file => {
      if (!file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = e => {
        const name = file.name.replace(/\.[^.]+$/,"").replace(/[-_]/g," ");
        const cap  = name.charAt(0).toUpperCase() + name.slice(1);
        const item = {
          id:          `up-${Date.now()}-${Math.random()}`,
          name:        cap,
          labels:      { en:cap, ml:cap, hi:cap, ta:cap, te:cap },
          color:       "#F0F4FF",
          emoji:       "📷",
          uploadedUrl: e.target.result,
          indicators:  [],
        };
        setUploadedCards(p => [item, ...p]);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const handlePickFromCollection = picked => {
    const newCards = picked.map(p => ({
      ...p,
      id: p.id,
      labels: p.labels || { en: p.name },
      indicators: p.indicators || [], // inherited from Collection — editable below
      uploadedUrl: undefined,
    }));
    setSelectedCards(prev => {
      const ex = new Set(prev.map(c => c.id));
      return [...prev, ...newCards.filter(c => !ex.has(c.id))];
    });
    setShowPicker(false);
  };

  const handleLoadSaved = async (s) => {
    setShowSaved(false);
    try {
      const res  = await fetch(`${API}/activitycard/${s._id}`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success) {
        const ac   = data.activityCard;
        const base = API.replace("/api","");
        setSetTitle(ac.title);
        setCustomSubtitle(ac.subtitle || "Activity Cards");
        setLanguage(ac.language || "en");
        setTheme(ac.theme || "white");
        setBorderStyle(ac.borderStyle || "solid");
        setLabelPosition(ac.labelPosition || "bottom");
        setFontSize(ac.fontSize || "lg");
        setShowPageNumber(ac.showPageNumber !== false);
        setSelectedCards((ac.cards||[]).map(c => ({
          ...c,
          id:          c._id || c.collectionId || `loaded-${Math.random()}`,
          imageUrl:    c.imageUrl?.startsWith("/uploads") ? `${base}${c.imageUrl}` : c.imageUrl,
          labels:      c.labels || { en: c.name },
          indicators:  c.indicators || [],
          uploadedUrl: undefined,
        })));
        setSetIndicators(ac.indicators || []);
        setSavedId(ac._id);
        setActiveTab("select");
      }
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      const base = API.replace("/api","");
      const body = {
        title:         setTitle || "My Activity Cards",
        subtitle:      customSubtitle,
        language,
        theme,
        borderStyle,
        labelPosition,
        fontSize,
        showPageNumber,
        indicators: setIndicators,
        cards: selectedCards.map(c => ({
          collectionId: (!c.id || c.id.startsWith("up-") || c.id.startsWith("loaded-")) ? null : c.id,
          name:         c.name,
          imageUrl:     c.imageUrl?.startsWith(base) ? c.imageUrl.replace(base,"") : (c.imageUrl || ""),
          color:        c.color || "#E8F0FE",
          emoji:        c.emoji || "",
          labels:       c.labels || { en: c.name },
          indicators:   c.indicators || [],
        })),
      };
      const method = savedId ? "PUT"  : "POST";
      const url    = savedId ? `${API}/activitycard/${savedId}` : `${API}/activitycard`;
      const res    = await fetch(url, { method, headers: getAuthHeader(), body: JSON.stringify(body) });
      const data   = await res.json();
      if (data.success) { setSavedId(data.activityCard._id); setSaveMsg("✅ Saved!"); }
      else setSaveMsg(`❌ ${data.message||"Save failed"}`);
    } catch { setSaveMsg("❌ Backend not reachable"); }
    setSaving(false);
    setTimeout(() => setSaveMsg(""), 5000);
  };

  const handlePrint = async () => {
    if (selectedCards.length === 0) return;
    await generatePDF(selectedCards, { language, theme, borderStyle, labelPosition, fontSize, showPageNumber, customSubtitle });
    if (savedId) { try { await fetch(`${API}/activitycard/${savedId}/use`, { method:"POST" }); } catch {} }
  };

  const getBorderPreview = () => ({
    solid:   `3px solid ${themeObj.accent}`,
    double:  `5px double ${themeObj.accent}`,
    dashed:  `3px dashed ${themeObj.accent}`,
    rounded: `3px solid ${themeObj.accent}`,
    none:    "none",
  }[borderStyle] || `3px solid ${themeObj.accent}`);

  const previewCard  = selectedCards[0];
  const previewLabel = previewCard ? (previewCard.labels?.[language] || previewCard.labels?.en || previewCard.name) : "";
  const previewSrc   = previewCard ? resolveUrl(previewCard) : "";

  // ─── UI ───────────────────────────────────────────────────────────────────
  return (
    <div style={{fontFamily:"'Segoe UI',Arial,sans-serif",background:"#F5F7FA",minHeight:"100vh",color:"#1a1a2e"}}>

      {/* TAB BAR + ACTIONS */}
      <div style={{background:"white",borderBottom:"1px solid #e0e7f0",padding:"0 24px",display:"flex",alignItems:"center"}}>
        {[["select","🖼️ Select"],["design","🎨 Design"],["export","🖨️ Export"]].map(([tab,label])=>(
          <button key={tab} onClick={()=>setActiveTab(tab)} style={{
            background:"none",border:"none",padding:"14px 18px",fontSize:12,fontWeight:700,
            color:activeTab===tab?"#003366":"#888",
            borderBottom:`3px solid ${activeTab===tab?"#003366":"transparent"}`,cursor:"pointer"
          }}>{label}</button>
        ))}
        <div style={{flex:1}}/>
        <span style={{fontSize:12,color:"#888",marginRight:12}}>
          <strong style={{color:"#003366"}}>{selectedCards.length}</strong> selected
        </span>
        <button onClick={()=>setShowSaved(true)}
          style={{padding:"7px 14px",background:"#e8f0fe",color:"#003366",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",marginRight:8}}>
          📂 Saved Sets
        </button>
        {saveMsg && <span style={{fontSize:11,fontWeight:700,color:saveMsg.startsWith("✅")?"#1B8A5A":"#c0392b",marginRight:10,maxWidth:260}}>{saveMsg}</span>}
        <button onClick={handleSave} disabled={saving}
          style={{padding:"7px 14px",background:saving?"#ccc":"#003366",color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:saving?"default":"pointer"}}>
          {saving?"Saving…":"💾 Save"}
        </button>
      </div>

      <div style={{maxWidth:1200,margin:"0 auto",padding:24}}>

        {/* ── SELECT TAB ── */}
        {activeTab==="select" && (
          <div>
            {/* Title input */}
            <div style={{display:"flex",gap:12,marginBottom:16,alignItems:"center"}}>
              <input value={setTitle} onChange={e=>setSetTitle(e.target.value)} placeholder="Card set title…"
                style={{border:"1.5px solid #dce3f5",borderRadius:8,padding:"8px 14px",fontSize:13,outline:"none",fontFamily:"inherit",flex:1,maxWidth:340}}/>
              <button onClick={()=>setShowPicker(true)}
                style={{padding:"9px 18px",background:"white",border:"2px solid #003366",borderRadius:10,fontWeight:700,fontSize:13,color:"#003366",cursor:"pointer"}}>
                🗂️ From Collection
              </button>
              <div onClick={()=>fileInputRef.current?.click()}
                style={{padding:"9px 18px",border:"2px dashed #aabbd6",borderRadius:10,cursor:"pointer",background:"white",fontWeight:700,color:"#555",fontSize:13}}>
                📁 Upload
                <input ref={fileInputRef} type="file" multiple accept="image/*" style={{display:"none"}} onChange={e=>handleFileUpload(e.target.files)}/>
              </div>
            </div>

            {/* Search + actions */}
            <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"center"}}>
              <div style={{position:"relative",flex:1,maxWidth:300}}>
                <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"#aaa"}}>🔍</span>
                <input value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder="Search uploaded images…"
                  style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:8,padding:"8px 10px 8px 32px",fontSize:12,outline:"none"}}/>
              </div>
              <button onClick={()=>setSelectedCards([])} style={{padding:"7px 14px",background:"white",color:"#666",border:"1px solid #dce3f5",borderRadius:7,fontSize:12,cursor:"pointer"}}>Clear Selection</button>
              {selectedCards.length>0 && (
                <button onClick={()=>setActiveTab("design")}
                  style={{marginLeft:"auto",padding:"8px 20px",background:"#003366",color:"white",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  Design Cards →
                </button>
              )}
            </div>

            {/* Selected chips */}
            {selectedCards.length>0 && (
              <div style={{background:"#e8f0fe",borderRadius:10,padding:"10px 14px",marginBottom:16,display:"flex",flexWrap:"wrap",gap:6,alignItems:"center"}}>
                <span style={{fontSize:11,fontWeight:700,color:"#003366",marginRight:4}}>Selected:</span>
                {selectedCards.map((c,i)=>(
                  <div key={i} onClick={()=>toggleCard(c)}
                    style={{display:"flex",alignItems:"center",gap:5,background:"white",border:"1.5px solid #003366",borderRadius:20,padding:"3px 8px 3px 4px",cursor:"pointer",fontSize:11}}>
                    <img src={resolveUrl(c)} style={{width:20,height:20,borderRadius:"50%",objectFit:"cover"}} alt=""/>
                    <span style={{fontWeight:600,color:"#003366"}}>{c.labels?.[language]||c.name}</span>
                    <span style={{color:"#aaa",fontSize:10}}>×</span>
                  </div>
                ))}
              </div>
            )}

            {/* Uploaded cards grid or empty state */}
            {filteredCards.length === 0 ? (
              <div style={{textAlign:"center",padding:"60px 20px",background:"white",borderRadius:12,border:"2px dashed #dce3f5",color:"#aaa"}}>
                <div style={{fontSize:40,marginBottom:12}}>🖼️</div>
                <div style={{fontSize:14,fontWeight:700,marginBottom:6,color:"#888"}}>No images yet</div>
                <div style={{fontSize:12,marginBottom:20}}>Browse your Collection Manager or upload images to get started</div>
                <div style={{display:"flex",gap:12,justifyContent:"center"}}>
                  <button onClick={()=>setShowPicker(true)}
                    style={{padding:"10px 20px",background:"#003366",color:"white",border:"none",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                    🗂️ Browse Collection
                  </button>
                  <button onClick={()=>fileInputRef.current?.click()}
                    style={{padding:"10px 20px",background:"white",color:"#003366",border:"2px solid #003366",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                    📁 Upload Images
                  </button>
                </div>
              </div>
            ) : (
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:12}}>
                {filteredCards.map((card,i)=>{
                  const sel = isSelected(card);
                  const src = resolveUrl(card);
                  return (
                    <div key={i} onClick={()=>toggleCard(card)} style={{
                      border:sel?"2.5px solid #003366":"2px solid #e0e7f0",
                      borderRadius:12,overflow:"hidden",cursor:"pointer",background:"white",
                      boxShadow:sel?"0 2px 16px rgba(0,51,102,.18)":"0 1px 4px rgba(0,0,0,.06)",
                      transition:"all .15s",position:"relative"
                    }}>
                      {sel && <div style={{position:"absolute",top:7,right:7,background:"#003366",color:"white",borderRadius:"50%",width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:900,zIndex:2}}>✓</div>}
                      {card.indicators?.length > 0 && (
                        <div style={{position:"absolute",top:7,left:7,background:"rgba(0,51,102,.85)",color:"white",fontSize:9,fontWeight:800,padding:"1px 6px",borderRadius:6,zIndex:2}}
                          title={card.indicators.join(", ")}>
                          🎯 {card.indicators.length}
                        </div>
                      )}
                      <div style={{height:110,display:"flex",alignItems:"center",justifyContent:"center",background:card.color||"#f8f9ff",overflow:"hidden"}}>
                        {src
                          ? <img src={src} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}} alt={card.name}/>
                          : <div style={{fontSize:32,color:"#ccc"}}>🖼️</div>
                        }
                      </div>
                      <div style={{padding:"8px 10px"}}>
                        <div style={{fontSize:11,fontWeight:700,color:"#1a1a2e",marginBottom:2}}>{card.labels?.[language]||card.name}</div>
                        <div style={{fontSize:9.5,color:"#aaa"}}>{card.name}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── DESIGN TAB ── */}
        {activeTab==="design" && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 340px",gap:28}}>

            {/* Live preview */}
            <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
              <div style={{fontWeight:700,fontSize:13,color:"#003366",marginBottom:16,alignSelf:"flex-start"}}>
                Live Preview
                {selectedCards.length>1 && <span style={{fontSize:11,fontWeight:400,color:"#888"}}> — card 1 of {selectedCards.length}</span>}
              </div>

              {selectedCards.length === 0 ? (
                <div style={{width:321,height:455,background:"#f5f7fa",borderRadius:6,border:"2px dashed #dce3f5",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:8,color:"#aaa"}}>
                  <div style={{fontSize:32}}>🖼️</div>
                  <div style={{fontSize:12}}>Select images first</div>
                </div>
              ) : (
                /* A4 mini @ 54% scale */
                <div style={{
                  width:321,height:455,background:themeObj.bg,borderRadius:6,
                  boxShadow:"0 8px 40px rgba(0,0,0,.18)",display:"flex",flexDirection:"column",
                  alignItems:"center",justifyContent:"space-between",padding:"15px 17px 11px",
                  border:"1px solid #dce3f5",overflow:"hidden"
                }}>
                  {/* mini header */}
                  <div style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8,flexShrink:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      <div style={{background:themeObj.accent,color:"white",fontWeight:900,fontSize:7,padding:"2px 5px",borderRadius:3}}>NISH</div>
                      <div style={{fontSize:5,color:themeObj.accent,lineHeight:1.4}}>
                        <div style={{fontWeight:700}}>National Institute of Speech &amp; Hearing</div>
                        <div style={{opacity:.7}}>Thiruvananthapuram · FSLP</div>
                      </div>
                    </div>
                    {customSubtitle && <div style={{fontSize:5,color:themeObj.accent,fontWeight:700}}>{customSubtitle}</div>}
                    <div style={{display:"flex",gap:3}}>
                      <div style={{background:"#b30000",color:"white",fontSize:4.5,fontWeight:700,padding:"1px 3px",borderRadius:2}}>NCAHT</div>
                      <div style={{background:"#006400",color:"white",fontSize:4.5,fontWeight:700,padding:"1px 3px",borderRadius:2}}>ICMR</div>
                    </div>
                  </div>
                  {/* label top */}
                  {labelPosition==="top" && <div style={{fontSize:fontPx*0.34,fontWeight:900,color:themeObj.text,textAlign:"center",letterSpacing:1,textTransform:"uppercase",padding:"0 0 6px",flexShrink:0}}>{previewLabel}</div>}
                  {/* image */}
                  <div style={{flex:1,width:"100%",display:"flex",alignItems:"center",justifyContent:"center",
                    border:getBorderPreview(),borderRadius:borderStyle==="rounded"?13:5,
                    overflow:"hidden",background:"white",margin:"4px 0",minHeight:0}}>
                    {previewSrc
                      ? <img src={previewSrc} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}} alt=""/>
                      : <div style={{color:"#ccc",fontSize:24}}>🖼️</div>
                    }
                  </div>
                  {/* label bottom */}
                  {labelPosition==="bottom" && <div style={{fontSize:fontPx*0.34,fontWeight:900,color:themeObj.text,textAlign:"center",letterSpacing:1,textTransform:"uppercase",padding:"6px 0 0",flexShrink:0}}>{previewLabel}</div>}
                  {/* mini footer */}
                  <div style={{width:"100%",borderTop:`1px solid ${themeObj.accent}30`,paddingTop:4,display:"flex",justifyContent:"space-between",marginTop:6,flexShrink:0}}>
                    <span style={{fontSize:4.5,color:themeObj.accent,opacity:.7}}>© NISH · NCAHT · ICMR</span>
                    {showPageNumber && <span style={{fontSize:4.5,color:themeObj.accent,opacity:.7}}>1 / {selectedCards.length||1}</span>}
                  </div>
                </div>
              )}

              {/* selected chips under preview */}
              {selectedCards.length>0 && (
                <div style={{marginTop:18,width:"100%"}}>
                  <div style={{fontWeight:700,fontSize:12,color:"#003366",marginBottom:8}}>Cards ({selectedCards.length})</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {selectedCards.map((c,i)=>(
                      <div key={i} onClick={()=>toggleCard(c)}
                        style={{display:"flex",alignItems:"center",gap:5,background:"white",border:"1.5px solid #003366",borderRadius:20,padding:"3px 9px 3px 4px",cursor:"pointer",fontSize:11}}>
                        <img src={resolveUrl(c)} style={{width:22,height:22,borderRadius:"50%",objectFit:"cover"}} alt=""/>
                        <span style={{fontWeight:600,color:"#003366"}}>{c.labels?.[language]||c.name}</span>
                        <span style={{color:"#aaa",fontSize:10}}>×</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Settings sidebar */}
            <div style={{display:"flex",flexDirection:"column",gap:12,overflowY:"auto",maxHeight:"calc(100vh - 130px)"}}>

              <div style={{background:"white",borderRadius:12,padding:16,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                <div style={{fontWeight:700,fontSize:11,color:"#003366",marginBottom:10}}>📝 SUBTITLE</div>
                <input value={customSubtitle} onChange={e=>setCustomSubtitle(e.target.value)} placeholder="e.g. Animals — Class 1"
                  style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:8,padding:"8px 10px",fontSize:12,outline:"none",fontFamily:"inherit"}}/>
              </div>

              <div style={{background:"white",borderRadius:12,padding:16,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                <div style={{fontWeight:700,fontSize:11,color:"#003366",marginBottom:10}}>🌐 LANGUAGE</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                  {LANGUAGES.map(lang=>(
                    <button key={lang.code} onClick={()=>setLanguage(lang.code)} style={{
                      padding:"7px 8px",border:language===lang.code?"2px solid #003366":"1.5px solid #dce3f5",
                      borderRadius:8,background:language===lang.code?"#e8f0fe":"white",
                      cursor:"pointer",fontSize:12,fontWeight:700,color:language===lang.code?"#003366":"#555"
                    }}>{lang.name}</button>
                  ))}
                </div>
              </div>

              <div style={{background:"white",borderRadius:12,padding:16,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                <div style={{fontWeight:700,fontSize:11,color:"#003366",marginBottom:10}}>🎨 THEME</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {THEMES.map(t=>(
                    <button key={t.id} onClick={()=>setTheme(t.id)} style={{
                      padding:"8px 10px",border:theme===t.id?`2px solid ${t.accent}`:"1.5px solid #dce3f5",
                      borderRadius:8,background:t.bg,cursor:"pointer",fontSize:11,fontWeight:700,color:t.text,
                      display:"flex",alignItems:"center",gap:6
                    }}>
                      <div style={{width:12,height:12,borderRadius:"50%",background:t.accent,flexShrink:0}}/>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{background:"white",borderRadius:12,padding:16,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                <div style={{fontWeight:700,fontSize:11,color:"#003366",marginBottom:10}}>🖼️ BORDER</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {BORDER_STYLES.map(bs=>(
                    <button key={bs.id} onClick={()=>setBorderStyle(bs.id)} style={{
                      padding:"6px 12px",border:borderStyle===bs.id?"2px solid #003366":"1.5px solid #dce3f5",
                      borderRadius:8,background:borderStyle===bs.id?"#e8f0fe":"white",
                      cursor:"pointer",fontSize:11,fontWeight:700,color:borderStyle===bs.id?"#003366":"#555"
                    }}>{bs.label}</button>
                  ))}
                </div>
              </div>

              <div style={{background:"white",borderRadius:12,padding:16,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                <div style={{fontWeight:700,fontSize:11,color:"#003366",marginBottom:10}}>🏷️ LABEL</div>
                <div style={{display:"flex",gap:6,marginBottom:10}}>
                  {LABEL_POSITIONS.map(lp=>(
                    <button key={lp.id} onClick={()=>setLabelPosition(lp.id)} style={{
                      flex:1,padding:"8px 4px",border:labelPosition===lp.id?"2px solid #003366":"1.5px solid #dce3f5",
                      borderRadius:8,background:labelPosition===lp.id?"#e8f0fe":"white",
                      cursor:"pointer",fontSize:11,fontWeight:700,color:labelPosition===lp.id?"#003366":"#555",textAlign:"center"
                    }}>{lp.icon}<br/>{lp.label}</button>
                  ))}
                </div>
                <div style={{fontWeight:700,fontSize:10,color:"#888",marginBottom:6}}>FONT SIZE</div>
                <div style={{display:"flex",gap:6}}>
                  {FONT_SIZES.map(fs=>(
                    <button key={fs.id} onClick={()=>setFontSize(fs.id)} style={{
                      flex:1,padding:"6px 4px",border:fontSize===fs.id?"2px solid #003366":"1.5px solid #dce3f5",
                      borderRadius:8,background:fontSize===fs.id?"#e8f0fe":"white",
                      cursor:"pointer",fontSize:11,fontWeight:700,color:fontSize===fs.id?"#003366":"#555",textAlign:"center"
                    }}>{fs.label}</button>
                  ))}
                </div>
              </div>

              <div style={{background:"white",borderRadius:12,padding:16,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                <div style={{fontWeight:700,fontSize:11,color:"#003366",marginBottom:10}}>⚙️ OPTIONS</div>
                <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                  <input type="checkbox" checked={showPageNumber} onChange={e=>setShowPageNumber(e.target.checked)} style={{width:16,height:16,accentColor:"#003366"}}/>
                  <span style={{fontSize:12,color:"#555",fontWeight:600}}>Show page numbers</span>
                </label>
              </div>

              <div style={{background:"white",borderRadius:12,padding:16,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                <div style={{fontWeight:700,fontSize:11,color:"#003366",marginBottom:4}}>🎯 DEVELOPMENT INDICATORS (NCF)</div>
                <div style={{fontSize:10,color:"#888",marginBottom:8,lineHeight:1.6}}>
                  What does this card set, as a whole, help practice? Individual
                  cards can carry their own tags too (inherited from Collection
                  when picked).
                </div>
                <IndicatorTagPicker taxonomy={taxonomy} selected={setIndicators} onToggle={toggleSetIndicator} compact/>
                {setIndicators.length > 0 && (
                  <div style={{display:"flex",flexWrap:"wrap",gap:5,marginTop:8}}>
                    {setIndicators.map(id => {
                      const ind = taxonomy?.indicators.find(i => i.id === id);
                      return (
                        <span key={id} style={{background:"#EBF4FB",color:"#003366",fontSize:10,
                                                fontWeight:700,padding:"2px 8px",borderRadius:10}}>
                          {id}{ind ? ` · ${ind.label}` : ""}
                        </span>
                      );
                    })}
                  </div>
                )}
                {touchedDomains.length > 0 && (
                  <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid #f0f4ff"}}>
                    <div style={{fontSize:9,fontWeight:700,color:"#8fa5bc",textTransform:"uppercase",
                                  letterSpacing:.6,marginBottom:6}}>Domains touched</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                      {touchedDomains.map(d => (
                        <span key={d.id} title={d.label}
                          style={{fontSize:11,fontWeight:700,color:"#003366",background:"#EBF4FB",
                                  padding:"2px 8px",borderRadius:8}}>
                          {d.icon} {d.short}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button onClick={()=>setActiveTab("export")}
                style={{padding:"12px",background:"#003366",color:"white",border:"none",borderRadius:10,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                Continue to Export →
              </button>
            </div>
          </div>
        )}

        {/* ── EXPORT TAB ── */}
        {activeTab==="export" && (
          <div style={{maxWidth:600,margin:"0 auto"}}>
            <div style={{background:"white",borderRadius:14,padding:28,boxShadow:"0 2px 16px rgba(0,0,0,.08)",marginBottom:20}}>
              <div style={{fontWeight:800,fontSize:20,color:"#003366",marginBottom:4}}>Ready to Print</div>
              <div style={{fontSize:13,color:"#888",marginBottom:22}}>Review settings and generate your PDF.</div>

              {/* mini preview strip */}
              {selectedCards.length > 0 && (
                <div style={{display:"flex",gap:8,marginBottom:22,overflowX:"auto",paddingBottom:6}}>
                  {selectedCards.slice(0,6).map((card,i)=>(
                    <div key={i} style={{minWidth:68,height:88,background:themeObj.bg,border:`2px solid ${themeObj.accent}`,
                      borderRadius:8,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:6,flexShrink:0}}>
                      <img src={resolveUrl(card)} style={{width:44,height:44,objectFit:"contain"}} alt=""/>
                      <div style={{fontSize:7.5,fontWeight:700,color:themeObj.text,textAlign:"center",marginTop:4,textTransform:"uppercase"}}>
                        {(card.labels?.[language]||card.name).slice(0,10)}
                      </div>
                    </div>
                  ))}
                  {selectedCards.length>6 && (
                    <div style={{minWidth:68,height:88,background:"#f0f4ff",border:"2px dashed #aabbd6",borderRadius:8,
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:"#aaa",flexShrink:0}}>
                      +{selectedCards.length-6}
                    </div>
                  )}
                </div>
              )}

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"5px 20px"}}>
                {[
                  ["Title",    setTitle],
                  ["Cards",   `${selectedCards.length} card${selectedCards.length!==1?"s":""}`],
                  ["Pages",   `${selectedCards.length} (1 per page)`],
                  ["Page Size","A4 Portrait"],
                  ["Theme",    THEMES.find(t=>t.id===theme)?.label],
                  ["Border",   BORDER_STYLES.find(b=>b.id===borderStyle)?.label],
                  ["Label",    `${LABEL_POSITIONS.find(l=>l.id===labelPosition)?.label} · ${FONT_SIZES.find(f=>f.id===fontSize)?.label}`],
                  ["Language", LANGUAGES.find(l=>l.code===language)?.name],
                ].map(([k,v])=>(
                  <div key={k} style={{display:"flex",gap:8,padding:"6px 0",borderBottom:"1px solid #f0f4ff",fontSize:13}}>
                    <span style={{color:"#888",minWidth:72}}>{k}:</span>
                    <span style={{fontWeight:700,color:"#1a1a2e"}}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {selectedCards.length===0 && (
              <div style={{background:"#fff8e1",border:"1.5px solid #ffc107",borderRadius:10,padding:14,marginBottom:16,fontSize:13,color:"#7a5800"}}>
                ⚠️ No cards selected.{" "}
                <button onClick={()=>setActiveTab("select")} style={{color:"#003366",fontWeight:700,background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>Select images first.</button>
              </div>
            )}

            <button onClick={handlePrint} disabled={selectedCards.length===0} style={{
              width:"100%",padding:18,background:selectedCards.length>0?"#003366":"#ccc",color:"white",
              border:"none",borderRadius:12,fontSize:16,fontWeight:800,
              cursor:selectedCards.length>0?"pointer":"not-allowed",
              boxShadow:selectedCards.length>0?"0 4px 20px rgba(0,51,102,.3)":"none"
            }}>
              🖨️ Generate &amp; Print — {selectedCards.length} Activity Card{selectedCards.length!==1?"s":""}
            </button>
            <div style={{textAlign:"center",fontSize:11,color:"#888",marginTop:8}}>
              Print dialog will open · Choose "Save as PDF" · A4 Portrait
            </div>
            <div style={{display:"flex",gap:10,marginTop:14}}>
              <button onClick={()=>setActiveTab("select")} style={{flex:1,padding:"10px",background:"white",color:"#003366",border:"1.5px solid #003366",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer"}}>← Change Images</button>
              <button onClick={()=>setActiveTab("design")} style={{flex:1,padding:"10px",background:"white",color:"#003366",border:"1.5px solid #003366",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer"}}>← Edit Design</button>
            </div>
          </div>
        )}
      </div>

      {/* STATUS BAR */}
      <div style={{position:"fixed",bottom:0,left:0,right:0,background:"white",borderTop:"1px solid #e0e7f0",padding:"8px 24px",display:"flex",alignItems:"center",gap:12,fontSize:12,color:"#666",zIndex:50}}>
        <span>{uploadedCards.length} uploaded</span>
        <span style={{color:"#ccc"}}>·</span>
        <span style={{color:selectedCards.length>0?"#003366":"#aaa",fontWeight:selectedCards.length>0?700:400}}>{selectedCards.length} selected</span>
        <span style={{color:"#ccc"}}>·</span>
        <span>Theme: <strong>{THEMES.find(t=>t.id===theme)?.label}</strong></span>
        <span style={{color:"#ccc"}}>·</span>
        <span>Lang: <strong>{LANGUAGES.find(l=>l.code===language)?.name}</strong></span>
        {savedId && <span style={{background:"#e8f0fe",color:"#003366",padding:"2px 8px",borderRadius:6,fontSize:11,fontWeight:700}}>📌 Saved</span>}
        <div style={{flex:1}}/>
        <button onClick={handlePrint} disabled={selectedCards.length===0} style={{
          background:selectedCards.length>0?"#003366":"#eee",color:selectedCards.length>0?"white":"#aaa",
          border:"none",borderRadius:6,padding:"6px 16px",fontWeight:700,fontSize:12,
          cursor:selectedCards.length>0?"pointer":"not-allowed"
        }}>🖨️ Print PDF →</button>
      </div>
      <div style={{height:52}}/>

      {showPicker && <CollectionPickerModal getAuthHeader={getAuthHeader} onPick={handlePickFromCollection} onClose={()=>setShowPicker(false)}/>}
      {showSaved  && <SavedSetsPanel        getAuthHeader={getAuthHeader} onLoad={handleLoadSaved}        onClose={()=>setShowSaved(false)}/>}
    </div>
  );
}